// ./src/components/generic/CurrencySelector/CurrencySelector.tsx
//
// THE currency picker for the whole app — never build another one. Backed by the
// shared CURRENCY_OPTIONS catalog, grouped Popular / All, with `compact` (code
// only, narrow) and `isoOnly` variants. Rendered through the shared SelectField,
// so on phones it automatically becomes the full-screen picker (150+ currencies
// in a dropdown is unusable with a thumb).
import React, { useCallback, useMemo } from "react";
import { SelectProps, Group, Text, ComboboxProps } from "@mantine/core";

import { SelectField } from "@/components/generic/form";

import {
  CURRENCY_OPTIONS,
  CURRENCY_MAP,
  CurrencyOption,
  getCurrencyDisplayLabel,
} from "@/config/currencyOptions";

// ============================================
// Types
// ============================================

export interface CurrencySelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Field name for form handling */
  name?: string;

  /** Selected currency code (ISO 4217) */
  value?: string | null;

  /** Callback when currency changes */
  onChange?: (name: string | undefined, value: string | null) => void;

  /** Show only priority currencies */
  priorityOnly?: boolean;

  /** Additional currencies to include (ISO codes) */
  includeCurrencies?: string[];

  /** Currencies to exclude (ISO codes) */
  excludeCurrencies?: string[];

  /**
   * Compact mode - shows only currency code in a small input.
   * Useful for inline currency selection next to amount fields.
   */
  compact?: boolean;

  /**
   * ISO-only mode - keeps the full-size input/dropdown but shows just the ISO
   * code (USD, CAD, EUR) instead of the full currency name. Unlike `compact`,
   * this keeps the label, normal width, and clear/asterisk behaviour.
   */
  isoOnly?: boolean;

  /**
   * Width for compact mode. Default is 100.
   */
  compactWidth?: number;

  /** Props passed to the underlying Combobox (e.g., withinPortal) */
  comboboxProps?: ComboboxProps;

  withinPortal?: boolean;
}

// ============================================
// Component
// ============================================

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  name,
  value,
  onChange,
  priorityOnly = false,
  includeCurrencies,
  excludeCurrencies,
  placeholder = "Select currency",
  label = "Currency",
  required = true,
  compact = false,
  isoOnly = false,
  compactWidth = 100,
  size = "md",
  comboboxProps,
  withinPortal,
  ...rest
}) => {
  // Handle change with proper typing
  const handleChange = useCallback(
    (newValue: string | null) => {
      onChange?.(name, newValue);
    },
    [name, onChange]
  );

  // Build filtered options list
  const options = useMemo(() => {
    let filtered: CurrencyOption[] = priorityOnly
      ? CURRENCY_OPTIONS.filter((c) => c.priority)
      : [...CURRENCY_OPTIONS];

    // Include additional currencies
    if (includeCurrencies?.length) {
      const additionalCurrencies = includeCurrencies
        .map((code) => CURRENCY_MAP[code])
        .filter(Boolean);
      filtered = [...filtered, ...additionalCurrencies];
    }

    // Exclude currencies
    if (excludeCurrencies?.length) {
      filtered = filtered.filter((c) => !excludeCurrencies.includes(c.value));
    }

    // Remove duplicates
    const seen = new Set<string>();
    filtered = filtered.filter((c) => {
      if (seen.has(c.value)) return false;
      seen.add(c.value);
      return true;
    });

    // Convert to Select data format with groups
    const priorityCurrencies = filtered.filter((c) => c.priority);
    const otherCurrencies = filtered.filter((c) => !c.priority);

    const data: any[] = [];

    if (priorityCurrencies.length > 0) {
      data.push({
        group: "Popular",
        items: priorityCurrencies.map((c) => ({
          value: c.value,
          label: compact || isoOnly ? c.value : getCurrencyDisplayLabel(c),
        })),
      });
    }

    if (otherCurrencies.length > 0) {
      data.push({
        group: "All Currencies",
        items: otherCurrencies.map((c) => ({
          value: c.value,
          label: compact || isoOnly ? c.value : getCurrencyDisplayLabel(c),
        })),
      });
    }

    return data;
  }, [priorityOnly, includeCurrencies, excludeCurrencies, compact, isoOnly]);

  // Render option in dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const currency = CURRENCY_MAP[option.value];
      if (!currency) return option.label;

      // In compact mode, show condensed info that fits narrow dropdown
      if (compact) {
        return (
          <Group gap="xs" wrap="nowrap">
            <Text size="md" fw={500}>
              {currency.value}
            </Text>
            {currency.symbol !== currency.value && (
              <Text size="xs" c="dimmed" truncate style={{ flex: 1 }}>
                {currency.symbol}
              </Text>
            )}
          </Group>
        );
      }

      // ISO-only mode: just the code, keeping the full-size input.
      if (isoOnly) {
        return (
          <Text size="md" fw={500}>
            {currency.value}
          </Text>
        );
      }

      return (
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            {currency.label}
          </Text>
          <Text size="md">({currency.value})</Text>
        </Group>
      );
    },
    [compact, isoOnly]
  );

  // Compact mode styling - input only, no dropdown overrides
  const selectStyles = useMemo(() => {
    if (!compact) return undefined;

    return {
      input: {
        textAlign: "center" as const,
        textTransform: "uppercase" as const,
        fontWeight: 500,
        paddingLeft: 8,
        paddingRight: 24, // Space for dropdown arrow
      },
    };
  }, [compact]);

  // Build combobox props - keep simple to avoid layout issues in drawers
  const finalComboboxProps = useMemo((): ComboboxProps => {
    const baseProps: ComboboxProps = {
      ...(comboboxProps || {}),
    };

    // Portal configuration - essential for dropdowns inside drawers
    if (withinPortal) {
      baseProps.withinPortal = true;
      baseProps.zIndex = 1000;
    }

    return baseProps;
  }, [withinPortal, comboboxProps]);

  return (
    <SelectField
      data={options}
      value={value}
      onChange={(_name, next) => handleChange(next)}
      // Long list → always the full-screen picker on phones, even in compact mode
      // (the trigger stays narrow; only the picker goes full-screen).
      placeholder={compact ? "---" : placeholder}
      label={compact ? undefined : label}
      clearable={!required}
      allowDeselect={!required}
      withAsterisk={!compact && required}
      renderOption={renderOption}
      nothingFoundMessage="No currency found"
      maxDropdownHeight={300}
      searchable={false}
      size={size}
      w={compact ? compactWidth : undefined}
      styles={selectStyles}
      comboboxProps={finalComboboxProps}
      {...rest}
    />
  );
};

export default CurrencySelector;